import {
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { AssistantBookingService } from "../../../services/assistant-booking.service";
import {
  AssistantConfirmPayload,
  AssistantRecommendation,
  AssistantStartResponse,
} from "../../../models/assistant.model";

type ChatMessage = {
  role: "ai" | "user";
  text: string;
};

@Component({
  selector: "app-ai-booking-card",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./ai-booking-card.component.html",
  styleUrls: ["./ai-booking-card.component.scss"],
})
export class AiBookingCardComponent {
  private readonly assistantService = inject(AssistantBookingService);
  private readonly router = inject(Router);

  @Input() locationQuery = "";
  @Input() isAuthenticated = false;

  @Output() searchRequested = new EventEmitter<{
    category?: string | null;
    location?: string | null;
  }>();

  prompt = "";

  messages = signal<ChatMessage[]>([
    {
      role: "ai",
      text: "Hi, what would you like to book today?",
    },
  ]);

  loading = signal(false);
  booking = signal(false);
  error = signal<string | null>(null);
  bookingError = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  result = signal<AssistantStartResponse | null>(null);

  quickPrompts = [
    "I want a haircut and beard trim tomorrow after work",
    "Find me a manicure near downtown Toronto on Saturday",
    "I need a facial this weekend",
    "Show me a budget-friendly haircut tonight",
  ];

  hasRecommendations = computed(() => {
    return (this.result()?.recommendations?.length ?? 0) > 0;
  });

  submit(): void {
    const finalPrompt = this.prompt.trim();
    if (!finalPrompt || this.loading()) return;

    this.messages.update((items) => [
      ...items,
      {
        role: "user",
        text: finalPrompt,
      },
    ]);

    this.prompt = "";
    this.loading.set(true);
    this.error.set(null);
    this.successMessage.set(null);
    this.bookingError.set(null);

    this.assistantService
      .start(finalPrompt, this.locationQuery || null)
      .subscribe({
        next: (res) => {
          this.result.set(res);

          this.messages.update((items) => [
            ...items,
            {
              role: "ai",
              text: res.assistant_reply,
            },
          ]);

          this.loading.set(false);
        },
        error: () => {
          this.error.set("Unable to get booking suggestions right now.");
          this.loading.set(false);
        },
      });
  }

  usePrompt(prompt: string): void {
    this.prompt = prompt;
    this.submit();
  }

  refine(prompt: string): void {
    const current = this.result();
    if (!current) return;

    this.messages.update((items) => [
      ...items,
      {
        role: "user",
        text: prompt,
      },
    ]);

    this.loading.set(true);
    this.error.set(null);

    this.assistantService.refine(current.session_id, prompt).subscribe({
      next: (res) => {
        this.result.set(res);

        this.messages.update((items) => [
          ...items,
          {
            role: "ai",
            text: res.assistant_reply,
          },
        ]);

        this.loading.set(false);
      },
      error: () => {
        this.error.set("Unable to refine results right now.");
        this.loading.set(false);
      },
    });
  }

  bookRecommendation(rec: AssistantRecommendation): void {
    const current = this.result();
    if (!current) return;

    if (!this.isAuthenticated) {
      this.bookingError.set("Please sign in to complete your booking.");
      return;
    }

    const payload: AssistantConfirmPayload = {
      session_id: current.session_id,
      salon_id: rec.salon_id,
      staff_id: rec.staff_id,
      slot_start: rec.slot_start,
      selected_service_ids: rec.matched_service_ids,
    };

    this.booking.set(true);
    this.bookingError.set(null);
    this.successMessage.set(null);

    this.assistantService.confirmBooking(payload).subscribe({
      next: (response) => {
        this.booking.set(false);
        this.successMessage.set(
          `Booked ${response.salon_name} with ${response.staff_name} successfully.`,
        );

        this.router.navigate(["/customer/appointments"], {
          queryParams: {
            created: response.appointment_id,
          },
        });
      },
      error: () => {
        this.booking.set(false);
        this.bookingError.set("Unable to confirm booking right now.");
      },
    });
  }

  chooseSlot(
    rec: AssistantRecommendation,
    slotStart: string,
    slotEnd: string,
  ): void {
    const updated: AssistantRecommendation = {
      ...rec,
      slot_start: slotStart,
      slot_end: slotEnd,
    };

    this.bookRecommendation(updated);
  }

  applyToSearch(): void {
    const res = this.result();
    if (!res) return;

    const services = res.parsed_request.services || [];
    const normalizedCategory = this.mapServicesToCategory(services);

    this.searchRequested.emit({
      category: normalizedCategory,
      location: res.parsed_request.location || this.locationQuery || null,
    });
  }

  private mapServicesToCategory(services: string[]): string | null {
    const values = services.map((x) => (x || "").toLowerCase());

    if (
      values.some((v) =>
        [
          "nails",
          "nail polish",
          "manicure",
          "pedicure",
          "gel polish",
          "shellac",
        ].includes(v),
      )
    ) {
      return "nails";
    }

    if (
      values.some((v) => ["hair", "haircut", "blowout", "color"].includes(v))
    ) {
      return "hair";
    }

    if (values.some((v) => ["facial"].includes(v))) return "facial";
    if (values.some((v) => ["massage"].includes(v))) return "massage";
    if (values.some((v) => ["brows"].includes(v))) return "brows";

    return services[0] || null;
  }

  trackBySalon(index: number, item: AssistantRecommendation): string {
    return `${item.salon_id}-${item.staff_id}-${item.slot_start}`;
  }
}
